import React, { useCallback, useEffect, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import utils from './utils';
import 'src/components/css/pagamento.css';

/**
 * Registro de MKR_HUB_PED_VENDA_PGTO_LINK
 * @typedef {Object} LinkPagamento
 * @property {number} id
 * @property {string} nomeResponsavel
 * @property {number} valor
 * @property {string} [url]
 * @property {string} status
 * @property {string} [dataPagamento]
 * @property {string} [dataExpiracao]
 */

export default function DialogLinksPagamento(props) {
  const { open, onClose, id_hub_ped_venda_pgto, crud } = props;

  /** @type {[LinkPagamento[], React.Dispatch<React.SetStateAction<LinkPagamento[]>>]} */
  const [links, setLinks] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [regerandoId, setRegerandoId] = useState(null);

  const carregarLinks = useCallback(() => {
    if (!id_hub_ped_venda_pgto) {
      setLinks([]);
      return;
    }

    setCarregando(true);

    utils.execute(
      crud || {},
      `HubPedidoVendaPagamentoLink?hubPedidoVendaPagamento=${id_hub_ped_venda_pgto}&sortBy=id&sortType=asc&size=-1`,
      null,
      function (res) {
        setLinks(res && res.data ? res.data : []);
        setCarregando(false);
      },
    );
  }, [id_hub_ped_venda_pgto, crud]);

  useEffect(() => {
    if (open) {
      carregarLinks();
    }
  }, [open, carregarLinks]);

  const avisarCopiado = () => {
    window.dialog.alert(utils.lng('copiado_area_transferencia', 'Copiado para área de transferência'));
  };

  const copiarLink = (event, link) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(link).then(avisarCopiado, (err) => console.error('Erro ao copiar link:', err));
      return;
    }

    const aux = document.createElement('textarea');
    const container = event.currentTarget.parentNode;

    aux.value = link;
    container.appendChild(aux);
    aux.select();

    const copiou = document.execCommand('copy');

    container.removeChild(aux);

    if (copiou) {
      avisarCopiado();
    }
  };

  // Recarrega a lista depois, para trazer a url e a expiração novas que o back gravou
  const regerarLink = (idLink) => {
    if (regerandoId) return;

    setRegerandoId(idLink);

    utils.execute(crud || {}, 'pedido/pagamento/link/integrar?id_link=' + idLink, null, function (res) {
      setRegerandoId(null);

      if (res && !res.error) {
        carregarLinks();
      }
    });
  };

  return (
    <Dialog open={Boolean(open)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{utils.lng('links_pagamento', 'Links de pagamento')}</DialogTitle>
      <DialogContent>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{utils.lng('nome_responsavel', 'Nome Responsável')}</TableCell>
              <TableCell align="right">{utils.lng('valor', 'Valor')}</TableCell>
              <TableCell align="center">{utils.lng('link', 'Link')}</TableCell>
              <TableCell align="center">{utils.lng('status', 'Status')}</TableCell>
              <TableCell align="center"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(carregando || links.length === 0) && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {carregando
                    ? utils.lng('carregando', 'Carregando...')
                    : utils.lng('nenhum_link_gerado', 'Nenhum link gerado para este pedido.')}
                </TableCell>
              </TableRow>
            )}
            {!carregando &&
              links.map((l) => {
                const pago = l.status === 'PAGO' || Boolean(l.dataPagamento);
                const dataExpiracao = l.dataExpiracao ? utils.toDate(l.dataExpiracao) : null;
                const erro =
                  l.status === 'ERRO' ||
                  l.status === 'EXPIRADO' ||
                  (utils.isValidDate(dataExpiracao) && dataExpiracao.getTime() < Date.now());

                return (
                  <TableRow key={'link-pagamento-' + l.id}>
                    <TableCell>{l.nomeResponsavel}</TableCell>
                    <TableCell align="right">{utils.money(l.valor)}</TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        color="primary"
                        startIcon={<i className="far fa-copy"></i>}
                        disabled={!l.url || pago || erro}
                        onClick={(e) => copiarLink(e, l.url)}
                      >
                        {utils.lng('copiar_link', 'Copiar link')}
                      </Button>
                    </TableCell>
                    <TableCell align="center">
                      <span
                        className={
                          'link-status ' +
                          (pago
                            ? 'link-status-pago'
                            : erro
                              ? 'link-status-erro'
                              : l.url
                                ? 'link-status-pendente'
                                : 'link-status-nao-gerado')
                        }
                      >
                        {pago
                          ? utils.lng('pago', 'Pago')
                          : erro
                            ? utils.lng('erro', 'Erro')
                            : l.url
                              ? utils.lng('pendente', 'Pendente')
                              : utils.lng('nao_gerado', 'Não gerado')}
                      </span>
                    </TableCell>
                    <TableCell align="center">
                      {/* Link já pago não tem o que regerar */}
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        disabled={l.status !== 'erro'}
                        startIcon={<i className={'fa fa-sync-alt' + (regerandoId === l.id ? ' fa-spin' : '')}></i>}
                        onClick={() => regerarLink(l.id)}
                      >
                        {utils.lng('regerar_link', 'Regerar link')}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" className="split-btn-fechar" onClick={onClose}>
          {utils.lng('fechar', 'Fechar')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
